import { reaction } from 'mobx'
import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import {
    skyDefaultBackgroundColor,
    skyDefaultBaseColor,
    skyDefaultBottomColor,
    skyDefaultHighlightColor,
    skyBottomEndY,
    skyBottomStartY,
    skyFocusDirection,
    skyGradientEnd,
    skyGradientPower,
    skyGradientStart,
    skyRadius,
    skySphereHeightSegments,
    skySphereWidthSegments,
    heightFogDensity,
    heightFogEnabled,
    heightFogFalloff,
    heightFogFar,
    heightFogHeight,
    heightFogNear,
    skyThemes,
    type SkyThemeConfig,
} from './config/constants'
import { useRunState } from './provider/useRunState'
import {
    HEIGHT_FOG_SHARED_UNIFORMS,
    updateHeightFogSettings,
} from './materials/heightFog'
import { Color, ShaderMaterial, BackSide, Vector3, Mesh, type ColorRepresentation } from 'three'

function createSkyMaterial() {
    return new ShaderMaterial({
        side: BackSide,
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
        uniforms: {
            uBottomColor: { value: new Color(skyDefaultBottomColor) },
            uBaseColor: { value: new Color(skyDefaultBaseColor) },
            uHighlightColor: { value: new Color(skyDefaultHighlightColor) },
            uFocusDirection: {
                value: new Vector3(...skyFocusDirection).normalize(),
            },
            uBottomStartY: { value: skyBottomStartY },
            uBottomEndY: { value: skyBottomEndY },
            uGradientStart: { value: skyGradientStart },
            uGradientEnd: { value: skyGradientEnd },
            uGradientPower: { value: skyGradientPower },
            uCameraWorldPosition: { value: new Vector3() },
            uHeightFogEnabled: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogEnabled,
            uHeightFogColor: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogColor,
            uHeightFogDensity: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogDensity,
            uHeightFogHeight: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogHeight,
            uHeightFogFalloff: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogFalloff,
            uHeightFogNear: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogNear,
            uHeightFogFar: HEIGHT_FOG_SHARED_UNIFORMS.uHeightFogFar,
        },
        vertexShader: `
            varying vec3 vSkyDirection;
            void main() {
                vSkyDirection = normalize(position);
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            uniform vec3 uBottomColor;
            uniform vec3 uBaseColor;
            uniform vec3 uHighlightColor;
            uniform vec3 uFocusDirection;
            uniform float uBottomStartY;
            uniform float uBottomEndY;
            uniform float uGradientStart;
            uniform float uGradientEnd;
            uniform float uGradientPower;
            uniform vec3 uCameraWorldPosition;
            uniform float uHeightFogEnabled;
            uniform vec3 uHeightFogColor;
            uniform float uHeightFogDensity;
            uniform float uHeightFogHeight;
            uniform float uHeightFogFalloff;
            uniform float uHeightFogNear;
            uniform float uHeightFogFar;
            varying vec3 vSkyDirection;

            void main() {
                vec3 worldDirection = normalize(vSkyDirection);
                vec3 focusDirection = normalize(uFocusDirection);
                float bottomGradient = clamp(
                    (worldDirection.y - uBottomStartY) / max(uBottomEndY - uBottomStartY, 0.0001),
                    0.0, 1.0
                );
                float alignment = dot(worldDirection, focusDirection);
                float gradient = smoothstep(uGradientStart, uGradientEnd, alignment);
                gradient = pow(clamp(gradient, 0.0, 1.0), uGradientPower);
                vec3 highlightedColor = mix(uBaseColor, uHighlightColor, gradient);
                vec3 bottomOverlayColor = highlightedColor * uBottomColor;
                vec3 color = mix(bottomOverlayColor, highlightedColor, bottomGradient);

                if (uHeightFogEnabled > 0.5) {
                    float virtualDepth = max(uHeightFogFar, 0.0001);
                    vec3 fogSamplePosition = uCameraWorldPosition + worldDirection * virtualDepth;
                    float heightAttenuation = exp(-max(fogSamplePosition.y - uHeightFogHeight, 0.0) * max(uHeightFogFalloff, 0.0001));
                    float baseDensity = max(uHeightFogDensity, 0.0);
                    float heightFogDensity = baseDensity * heightAttenuation;
                    float heightFogFactor = 1.0 - exp(-heightFogDensity * heightFogDensity * virtualDepth * virtualDepth);
                    color = mix(color, uHeightFogColor, clamp(heightFogFactor, 0.0, 1.0));
                }

                gl_FragColor = vec4(color, 1.0);
            }
        `,
    })
}

export function Sky() {
    const { camera, scene } = useThree()
    const runState = useRunState()

    const meshRef = useRef<Mesh>(null)
    const materialRef = useRef<ShaderMaterial>(createSkyMaterial())
    const _cameraWorldPos = useRef(new Vector3())
    const backgroundColorRef = useRef(new Color(skyDefaultBackgroundColor))
    const previousPhaseRef = useRef(runState.phase)

    const applyGeneralSettings = () => {
        const mat = materialRef.current
        mat.uniforms.uFocusDirection.value
            .set(
                skyFocusDirection[0],
                skyFocusDirection[1],
                skyFocusDirection[2],
            )
            .normalize()
        mat.uniforms.uBottomStartY.value = skyBottomStartY
        mat.uniforms.uBottomEndY.value = skyBottomEndY
        mat.uniforms.uGradientStart.value = skyGradientStart
        mat.uniforms.uGradientEnd.value = skyGradientEnd
        mat.uniforms.uGradientPower.value = skyGradientPower
    }

    const applyFogSettings = (color: ColorRepresentation) => {
        updateHeightFogSettings({
            enabled: heightFogEnabled,
            color,
            density: heightFogDensity,
            height: heightFogHeight,
            falloff: heightFogFalloff,
            near: heightFogNear,
            far: heightFogFar,
        })
    }

    const applyThemeImmediate = (theme: SkyThemeConfig) => {
        const mat = materialRef.current
        mat.uniforms.uBottomColor.value.set(theme.bottomColor)
        mat.uniforms.uBaseColor.value.set(theme.baseColor)
        mat.uniforms.uHighlightColor.value.set(theme.highlightColor)
        applyFogSettings(theme.fogColor)
        backgroundColorRef.current.set(theme.baseColor)
        scene.background = backgroundColorRef.current
    }

    const applyWeightedTheme = (weights: number[]) => {
        const themes = skyThemes.length > 0
            ? skyThemes
            : [skyThemes[0] ?? {
                bottomColor: skyDefaultBottomColor,
                baseColor: skyDefaultBaseColor,
                highlightColor: skyDefaultHighlightColor,
                fogColor: skyDefaultBaseColor,
            }]
        const normalizedWeights = themes.map((_, index) => Math.max(weights[index] ?? 0, 0))
        const totalWeight = normalizedWeights.reduce((sum, weight) => sum + weight, 0)
        const fallbackIndex = Math.min(
            Math.max(runState.trackRegionState.activeRegion - 1, 0),
            themes.length - 1,
        )

        if (totalWeight <= 0) {
            applyThemeImmediate(themes[fallbackIndex] ?? themes[0])
            return
        }

        const bottomColor = new Color(0x000000)
        const baseColor = new Color(0x000000)
        const highlightColor = new Color(0x000000)
        const fogColor = new Color(0x000000)

        themes.forEach((theme, index) => {
            const weight = normalizedWeights[index] / totalWeight
            if (weight <= 0) return

            bottomColor.add(new Color(theme.bottomColor).multiplyScalar(weight))
            baseColor.add(new Color(theme.baseColor).multiplyScalar(weight))
            highlightColor.add(new Color(theme.highlightColor).multiplyScalar(weight))
            fogColor.add(new Color(theme.fogColor).multiplyScalar(weight))
        })

        const mat = materialRef.current
        mat.uniforms.uBottomColor.value.copy(bottomColor)
        mat.uniforms.uBaseColor.value.copy(baseColor)
        mat.uniforms.uHighlightColor.value.copy(highlightColor)
        applyFogSettings(fogColor)
        backgroundColorRef.current.copy(baseColor)
        scene.background = backgroundColorRef.current
    }

    const syncTheme = (
        currentPhase = runState.phase,
        previousPhase = previousPhaseRef.current,
    ) => {
        if (currentPhase === 'idle') {
            applyThemeImmediate(skyThemes[0])
            previousPhaseRef.current = currentPhase
            return
        }

        if (currentPhase === 'running' && previousPhase === 'idle') {
            applyThemeImmediate(skyThemes[0])
            previousPhaseRef.current = currentPhase
            return
        }

        applyWeightedTheme(
            runState.trackRegionState.regions.map((region) => region.weight),
        )

        previousPhaseRef.current = currentPhase
    }

    useEffect(() => {
        applyGeneralSettings()
        syncTheme()

        const disposeReaction = reaction(
            () => [
                runState.phase,
                runState.trackRegionState.activeRegion,
                runState.trackRegionState.activeTransition,
                runState.trackRegionState.transitionProgress,
                runState.trackRegionState.regions.map(region => region.weight).join('|'),
            ] as const,
            () => {
                applyGeneralSettings()
                syncTheme()
            },
        )

        return () => {
            disposeReaction()
        }
    }, [runState])

    useFrame(() => {
        const mesh = meshRef.current
        const mat = materialRef.current
        if (!mesh) return

        // Keep the dome centered on the camera so it behaves like a distant sky.
        camera.getWorldPosition(_cameraWorldPos.current)
        mesh.position.copy(_cameraWorldPos.current)
        mat.uniforms.uCameraWorldPosition.value.copy(_cameraWorldPos.current)

    })

    return (
        <>
            <color attach='background' args={[skyDefaultBackgroundColor]} />
            <mesh
                ref={meshRef}
                material={materialRef.current}
                frustumCulled={false}
                renderOrder={-1000}
            >
                <sphereGeometry
                    args={[
                        skyRadius,
                        skySphereWidthSegments,
                        skySphereHeightSegments,
                    ]}
                />
            </mesh>
        </>
    )
}
